import { useState, useRef, useCallback, useEffect } from 'react'
import type { ComponentProps } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { Search } from 'lucide-react'

import arrowImg from '/images/pages/home/arrow.webp'
import blobbyImg from '/images/blobby/small-scale-blobby.svg'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'

import type {
  TypewriterScenario,
  ScenarioBlock,
  ChartConfig,
  BarChartConfig,
  BarHorizontalChartConfig,
  PieChartConfig,
  ChartLegend,
  TooltipConfig,
} from './scenario'

import defaultScenario from './scenarios/enterprise-sales.json'

const fallbackScenario = defaultScenario as unknown as TypewriterScenario

type RenderedBlock = {
  id: string
  delayMs: number
  node: React.ReactNode
}

type RectRadius = number | [number, number, number, number]

type TooltipFormatter = NonNullable<ComponentProps<typeof Tooltip>['formatter']>

const CHART_ANIMATION_MS: Record<string, number> = {
  bar: 1000,
  'bar-horizontal': 1000,
  pie: 2500,
}

const FALLBACK_PIE_COLORS = [
  'hsl(202, 100%, 50%)',
  'hsl(214, 32%, 75%)',
  'hsl(142, 76%, 36%)',
  'hsl(0, 84%, 60%)',
]

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(0, 0%, 100%)',
  border: '1px solid hsl(214, 32%, 91%)',
  borderRadius: '8px',
  color: 'hsla(334, 62%, 19%, 1)',
}

const coerceNumber = (
  rawValue: number | string | ReadonlyArray<number | string> | undefined,
): number => {
  if (Array.isArray(rawValue)) {
    return coerceNumber(rawValue[0])
  }
  if (typeof rawValue === 'number') {
    return rawValue
  }
  if (typeof rawValue === 'string') {
    const parsed = Number(rawValue)
    return Number.isNaN(parsed) ? 0 : parsed
  }
  return 0
}

const formatTooltipValue = (
  rawValue: number | string | ReadonlyArray<number | string> | undefined,
  tooltip?: TooltipConfig,
): string => {
  const numericValue = coerceNumber(rawValue)
  const prefix = tooltip?.prefix ?? ''
  const suffix = tooltip?.suffix ?? ''
  const decimals = tooltip?.decimals

  const formattedValue =
    typeof decimals === 'number'
      ? numericValue.toFixed(decimals)
      : `${numericValue}`

  return `${prefix}${formattedValue}${suffix}`
}

const resolveBarRadius = (
  radius?: number | number[] | [number, number, number, number],
): RectRadius | undefined => {
  if (Array.isArray(radius)) {
    return radius.length === 4
      ? (radius as [number, number, number, number])
      : undefined
  }
  return radius
}

const LegendList = ({ legend }: { legend: ChartLegend }) => {
  const items = legend.items ?? []
  if (!items.length) return null

  const size = legend.size ?? 'large'
  const columnClass = size === 'small' ? 'legendColumnSmall' : 'legendColumn'
  const dotClassBase = size === 'small' ? 'legendDotSmall' : 'legendDot'
  const textClass = size === 'small' ? undefined : 'legendText'

  return (
    <div className={columnClass}>
      {items.map((item, index) => {
        const dotClassName = [
          dotClassBase,
          item.dotClass === 'secondary' ? 'legendDotSecondary' : '',
          item.dotClass === 'border' ? 'legendDotBorder' : '',
        ]
          .filter(Boolean)
          .join(' ')

        return (
          <div className="legendItem" key={`${item.text}-${index}`}>
            <div
              className={dotClassName}
              style={item.color ? { backgroundColor: item.color } : undefined}
            />
            <span className={textClass}>{item.text}</span>
          </div>
        )
      })}
      {legend.footer ? (
        <div className="legendFooter">
          <p className="legendFooterText">{legend.footer}</p>
        </div>
      ) : null}
    </div>
  )
}

const BarChartCard = ({ config }: { config: BarChartConfig }) => {
  const heightPercent = config.heightPercent ?? 85
  const height = config.height ?? (`${heightPercent}%` as `${number}%`)
  const tooltipFormatter: TooltipFormatter | undefined = config.tooltip
    ? (value) => formatTooltipValue(value, config.tooltip)
    : undefined

  return (
    <div className={config.containerClass ?? 'chartCard'}>
      <h3 className="chartTitle">{config.title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={config.data}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
          <XAxis
            dataKey={config.xKey}
            tick={{ fontSize: config.xTickFontSize ?? 12 }}
            stroke="hsl(218, 15%, 50%)"
          />
          <YAxis
            tick={{ fontSize: config.yTickFontSize ?? 12 }}
            stroke="hsl(218, 15%, 50%)"
            width="auto"
          />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            formatter={tooltipFormatter}
            cursor={{ fill: 'rgb(108, 122, 147, 0.33)' }}
          />
          {config.legend?.show ? <Legend /> : null}
          <Bar
            dataKey={config.yKey}
            fill={config.barColor ?? 'hsl(202, 100%, 50%)'}
            radius={resolveBarRadius(config.barRadius) ?? [4, 4, 0, 0]}
            name={config.barName}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const BarHorizontalChartCard = ({
  config,
}: {
  config: BarHorizontalChartConfig
}) => {
  const tooltipFormatter: TooltipFormatter | undefined = config.tooltip
    ? (value) => formatTooltipValue(value, config.tooltip)
    : undefined

  return (
    <div className={config.containerClass ?? 'chartCardSmall'}>
      <h3 className="chartTitle">{config.title}</h3>
      <ResponsiveContainer width="100%" height={config.height ?? 180}>
        <BarChart data={config.data} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
          <XAxis
            type="number"
            tick={{ fontSize: config.xTickFontSize ?? 11 }}
            stroke="hsl(218, 15%, 50%)"
          />
          <YAxis
            type="category"
            dataKey={config.categoryKey}
            tick={{ fontSize: config.yTickFontSize ?? 10 }}
            stroke="hsl(218, 15%, 50%)"
            width={config.yAxisWidth ?? 'auto'}
          />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            formatter={tooltipFormatter}
            cursor={{ fill: 'rgb(108, 122, 147, 0.33)' }}
          />
          <Bar
            dataKey={config.valueKey}
            fill={config.barColor ?? 'hsl(202, 100%, 50%)'}
            radius={resolveBarRadius(config.barRadius) ?? [0, 4, 4, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const PieChartCard = ({ config }: { config: PieChartConfig }) => {
  const hasLegend = Boolean(config.legend?.items?.length)
  const isSideLayout = config.layout === 'side' && hasLegend
  const flexClass = config.flexClass ?? 'chartFlex'
  const tooltipFormatter: TooltipFormatter | undefined = config.tooltip
    ? (value, name) => {
        const formatted = formatTooltipValue(value, config.tooltip)
        if (config.tooltip?.includeLabel && typeof name === 'string') {
          return [formatted, name]
        }
        return formatted
      }
    : undefined

  return (
    <div className={config.containerClass ?? 'chartCard'}>
      <h3 className="chartTitle">{config.title}</h3>
      {isSideLayout ? (
        <div className={flexClass}>
          <ResponsiveContainer width="50%" height="100%">
            <PieChart>
              <Pie
                data={config.data}
                cx="50%"
                cy="50%"
                innerRadius={config.innerRadius ?? 30}
                outerRadius={config.outerRadius ?? 55}
                paddingAngle={config.paddingAngle ?? 2}
                dataKey="value"
              >
                {config.data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.color ??
                      FALLBACK_PIE_COLORS[index % FALLBACK_PIE_COLORS.length]
                    }
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={tooltipFormatter}
              />
            </PieChart>
          </ResponsiveContainer>
          {config.legend ? <LegendList legend={config.legend} /> : null}
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height="85%">
            <PieChart>
              <Pie
                data={config.data}
                cx="50%"
                cy="50%"
                innerRadius={config.innerRadius ?? 30}
                outerRadius={config.outerRadius ?? 55}
                paddingAngle={config.paddingAngle ?? 2}
                dataKey="value"
              >
                {config.data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.color ??
                      FALLBACK_PIE_COLORS[index % FALLBACK_PIE_COLORS.length]
                    }
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={tooltipFormatter}
              />
            </PieChart>
          </ResponsiveContainer>
          {config.legend ? <LegendList legend={config.legend} /> : null}
        </>
      )}
    </div>
  )
}

const ChartRenderer = ({ config }: { config: ChartConfig }) => {
  switch (config.type) {
    case 'bar':
      return <BarChartCard config={config} />
    case 'bar-horizontal':
      return <BarHorizontalChartCard config={config} />
    case 'pie':
      return <PieChartCard config={config} />
    default:
      return null
  }
}

const renderBlock = (
  scenario: TypewriterScenario,
  block: ScenarioBlock,
): React.ReactNode => {
  switch (block.type) {
    case 'chart': {
      const chart = scenario.charts[block.chartId]
      if (!chart) {
        return <p className="paragraphItalic">Missing chart: {block.chartId}</p>
      }
      return <ChartRenderer config={chart} />
    }
    case 'heading':
      return (
        <p className="paragraph">
          <strong className="bold">{block.text}</strong>
        </p>
      )
    case 'paragraph':
      if (block.emphasis === 'bold') {
        return (
          <p className="paragraph">
            <strong className="bold">{block.text}</strong>
          </p>
        )
      }
      if (block.emphasis === 'italic') {
        return <p className="paragraphItalic">{block.text}</p>
      }
      return <p className="paragraph">{block.text}</p>
    case 'list':
      return (
        <ul className="listContainer">
          {block.items.map((item) => (
            <li className="listItem" key={item}>
              {item}
            </li>
          ))}
        </ul>
      )
    default:
      return null
  }
}

const buildRenderedBlocks = (
  scenario: TypewriterScenario,
  blocks: ScenarioBlock[],
): RenderedBlock[] =>
  blocks.map((block, index) => ({
    id: `${block.type}-${index}`,
    delayMs: block.delayMs ?? 0,
    node: renderBlock(scenario, block),
  }))

// Blobby Avatar component
const BlobbyAvatar = ({ isThinking = false }: { isThinking?: boolean }) => (
  <div
    className={`avatarContainer ${isThinking ? 'avatarContainerThinking' : ''}`}
  >
    <img src={blobbyImg} alt="Blobby" className="avatarImage" />
    {isThinking && (
      <div className="thinkingDots">
        <span className="thinkingDot" style={{ animationDelay: '0ms' }} />
        <span className="thinkingDot" style={{ animationDelay: '150ms' }} />
        <span className="thinkingDot" style={{ animationDelay: '300ms' }} />
      </div>
    )}
  </div>
)

const ResponseBlobby = () => (
  <svg
    className="responseBlobby"
    viewBox="0 0 92 92"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_response_blobby)">
      <path
        d="M45.8248 91.6484C72.4733 91.6484 91.6497 71.9086 91.6497 45.8236C91.6497 19.7386 72.4733 0 45.8248 0C19.1764 0 0 19.7398 0 45.8248C0 71.9099 19.1751 91.6497 45.8248 91.6497V91.6484Z"
        fill="#FF5FA2"
      />
      <path
        className="winkEyeOpen"
        d="M43.5459 41.1439C43.5459 43.9725 41.0518 46.1433 37.8622 46.0917C35.1682 46.0502 33.1773 44.0605 33.254 41.4935C33.3357 38.7995 35.9329 36.5406 39.0244 36.4689C41.57 36.4123 43.5446 38.4536 43.5446 41.1439H43.5459Z"
        fill="#4D122C"
      />
      <path
        className="winkEyeClosed"
        d="M33.5 41.3 Q38.4 39 43.3 41.3"
        stroke="#4D122C"
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
        opacity="0"
      />
      <path
        d="M80.5328 41.1439C80.5328 43.9725 78.0387 46.1433 74.8492 46.0917C72.1539 46.0502 70.1642 44.0605 70.2409 41.4935C70.3227 38.7995 72.9198 36.5406 76.0113 36.4689C78.5569 36.4123 80.5315 38.4536 80.5315 41.1439H80.5328Z"
        fill="#4D122C"
      />
      <path
        d="M48.3818 44.8365C47.1643 45.5245 47.0298 46.9155 48.8484 48.7782C50.6671 50.6409 53.5686 52.53 56.9808 52.4696C60.3942 52.4558 63.4392 50.6132 65.3509 48.3128C65.571 48.0525 66.3822 47.2362 66.3294 46.2942C66.2992 45.854 65.9508 44.6931 64.5988 44.7246C63.8907 44.7334 63.526 45.0981 62.794 45.693C60.9313 47.1155 58.9064 47.9368 56.9393 47.9456C54.9735 47.972 52.9083 47.1193 51.2406 45.6691C50.418 44.9547 49.598 44.146 48.3806 44.8353L48.3818 44.8365Z"
        fill="#4D122C"
      />
    </g>
    <defs>
      <clipPath id="clip0_response_blobby">
        <rect width="91.6484" height="91.6484" fill="white" />
      </clipPath>
    </defs>
  </svg>
)

const ResponseContent = ({
  scrollToBottom,
  blocks,
}: {
  scrollToBottom: () => void
  blocks: RenderedBlock[]
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      const container = containerRef.current
      if (!container) return

      const items = Array.from(container.children) as HTMLElement[]
      if (!items.length) return

      gsap.set(items.slice(1), { display: 'none' })
      scrollToBottom()

      const tl = gsap.timeline()
      items.forEach((item, idx) => {
        if (idx === 0) return
        const delay = blocks[idx]?.delayMs ?? 0
        if (delay > 0) {
          tl.to({}, { duration: delay / 1000 })
        }
        tl.call(() => {
          item.style.display = 'block'
          scrollToBottom()
        })
      })
    },
    { scope: containerRef, dependencies: [blocks] },
  )

  return (
    <div className="responseContent" ref={containerRef}>
      {blocks.map((block) => (
        <div key={block.id}>{block.node}</div>
      ))}
    </div>
  )
}

// Chat input component
const ChatInput = ({
  displayedText,
  isTyping,
  showNudge = false,
}: {
  displayedText: string
  isTyping: boolean
  showNudge?: boolean
}) => (
  <div className="chatInputContainer">
    <Search className="searchIcon" />
    <div className="chatInputBox">
      <span className="chatInputText">
        {displayedText}
        {isTyping && <span className="cursor" />}
      </span>
    </div>
    <div className="arrowContainer">
      <img
        src={arrowImg}
        alt="arrow"
        className={showNudge ? 'arrowNudge submit-arrow' : 'submit-arrow'}
      />
    </div>
  </div>
)

// Message types for the chat history
type ChatMessage =
  | { type: 'question'; text: string }
  | { type: 'thinking' }
  | {
      type: 'response'
      content: RenderedBlock[]
    }

type TypewriterInputProps = {
  scenario?: TypewriterScenario
}

export const TypewriterInput = ({
  scenario = fallbackScenario,
}: TypewriterInputProps) => {
  const activeScenario = scenario ?? fallbackScenario
  const conversations = activeScenario.conversations ?? []

  const timing = {
    typingSpeedMs: activeScenario.timing?.typingSpeedMs ?? 32,
    thinkingMs: activeScenario.timing?.thinkingMs ?? 1300,
    nudgeDelayMs: activeScenario.timing?.nudgeDelayMs ?? 600,
  }

  const [displayedText, setDisplayedText] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [showNudge, setShowNudge] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [resetKey, setResetKey] = useState(0)
  const [isVisible, setIsVisible] = useState(
    () => typeof IntersectionObserver === 'undefined',
  )

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<gsap.core.Timeline | null>(null)
  const isVisibleRef = useRef(false)

  const scrollToBottom = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    gsap.to(el, {
      scrollTop: el.scrollHeight - el.clientHeight,
      duration: 0.5,
      ease: 'power3.out',
    })
  }, [])

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return

    if (typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
      },
      { threshold: 0.15 },
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    isVisibleRef.current = isVisible

    if (isVisible) {
      timelineRef.current?.play()
      return
    }

    timelineRef.current?.pause()
  }, [isVisible])

  useGSAP(
    () => {
      timelineRef.current?.kill()
      setDisplayedText('')
      setMessages([])
      setShowNudge(false)
      setIsTyping(false)
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0
      }

      if (!conversations.length) return

      const tl = gsap.timeline({ paused: !isVisibleRef.current })
      timelineRef.current = tl

      conversations.forEach((conversation, convIdx) => {
        const question = conversation.question || ''
        const isLast = convIdx === conversations.length - 1

        // ── TYPING ──
        tl.addLabel(`conv-${convIdx}-type`)
        tl.call(() => setIsTyping(true))
        tl.call(() => scrollToBottom(), [], '+=0.05')
        tl.to({}, { duration: 0.5 })
        for (let i = 1; i <= question.length; i++) {
          const text = question.slice(0, i)
          tl.call(() => setDisplayedText(text))
          tl.to({}, { duration: timing.typingSpeedMs / 1000 })
        }

        // ── NUDGE ──
        tl.addLabel(`conv-${convIdx}-nudge`)
        tl.call(() => {
          setShowNudge(true)
          setIsTyping(false)
        })
        tl.to({}, { duration: timing.nudgeDelayMs / 1000 })

        // ── COMMIT QUESTION ──
        tl.addLabel(`conv-${convIdx}-commit`)
        tl.call(() => {
          setShowNudge(false)
          setMessages((prev) => [...prev, { type: 'question', text: question }])
          setDisplayedText('')
        })
        tl.call(() => scrollToBottom(), [], '+=0.05')

        // ── THINKING ──
        tl.addLabel(`conv-${convIdx}-thinking`)
        tl.call(() => {
          setMessages((prev) => [...prev, { type: 'thinking' }])
        })
        tl.call(() => scrollToBottom(), [], '+=0.05')
        tl.to({}, { duration: timing.thinkingMs / 1000 })

        // ── RESPONSE ──
        tl.addLabel(`conv-${convIdx}-response`)
        tl.call(() => {
          setMessages((prev) => [
            ...prev.filter((m) => m.type !== 'thinking'),
            {
              type: 'response',
              content: buildRenderedBlocks(activeScenario, conversation.blocks),
            },
          ])
        })
        tl.call(
          () => {
            const containers =
              scrollContainerRef.current?.querySelectorAll('.responseContainer')
            const latest = containers?.[containers.length - 1]
            const bubble = latest?.querySelector('.responseBubble')
            if (bubble) {
              gsap.fromTo(
                bubble,
                { opacity: 0, scale: 0.96 },
                { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' },
              )
            }
            scrollToBottom()
          },
          [],
          '+=0.05',
        )

        // Wait for block reveals (skip first block — shown immediately)
        const blockRevealTime = conversation.blocks
          .slice(1)
          .reduce((sum, b) => sum + (b.delayMs ?? 0), 0)

        // Each chart finishes at: (time it appears) + (its animation duration).
        // Find whichever chart extends furthest past the last block reveal.
        let chartPadding = 0
        let cumulativeDelay = 0
        conversation.blocks.forEach((block, idx) => {
          if (idx > 0) cumulativeDelay += block.delayMs ?? 0
          if (block.type === 'chart') {
            const chartConfig = activeScenario.charts[block.chartId]
            const animMs = chartConfig
              ? (CHART_ANIMATION_MS[chartConfig.type] ?? 1500)
              : 1500
            const overshoot = cumulativeDelay + animMs - blockRevealTime
            if (overshoot > chartPadding) chartPadding = overshoot
          }
        })

        // Merge chart animation wait and pauseAfterResponse into one pause
        const postResponseWait = Math.max(
          blockRevealTime + chartPadding,
          conversation.pauseAfterResponseMs ?? 0,
        )
        const postResponseSec = postResponseWait / 1000
        const winkAt = Math.max(postResponseSec - 0.75, 0)
        if (winkAt > 0) {
          tl.to({}, { duration: winkAt })
        }

        // ── WINK ──
        tl.call(() => {
          const containers =
            scrollContainerRef.current?.querySelectorAll('.responseContainer')
          const latest = containers?.[containers.length - 1]
          const openEye = latest?.querySelector('.winkEyeOpen')
          const closedEye = latest?.querySelector('.winkEyeClosed')
          if (openEye && closedEye) {
            const winkTl = gsap.timeline()
            winkTl
              .to(openEye, { opacity: 0, duration: 0.12, ease: 'power2.in' })
              .to(
                closedEye,
                { opacity: 1, duration: 0.12, ease: 'power2.out' },
                '<',
              )
              .to(closedEye, {
                opacity: 0,
                duration: 0.12,
                ease: 'power2.in',
                delay: 0.35,
              })
              .to(
                openEye,
                { opacity: 1, duration: 0.12, ease: 'power2.out' },
                '<',
              )
          }
        })

        const remainingPause = postResponseSec - winkAt
        if (remainingPause > 0) {
          tl.to({}, { duration: remainingPause })
        }

        if (isLast) {
          tl.to({}, { duration: 1.2 })
          tl.call(() => setResetKey((key) => key + 1))
        }
      })
    },
    { dependencies: [activeScenario, resetKey] },
  )

  if (!conversations.length) {
    return null
  }

  return (
    <>
      <div className="typewriterInput">
        <div className="container" ref={scrollContainerRef}>
          <div className="contentWrapper messageSpacing">
            {messages.map((message, idx) => {
              if (message.type === 'question') {
                return (
                  <div key={`question-${idx}`} className="questionContainer">
                    <ChatInput displayedText={message.text} isTyping={false} />
                  </div>
                )
              }
              if (message.type === 'thinking') {
                return (
                  <div key={`thinking-${idx}`} className="thinkingContainer">
                    <BlobbyAvatar isThinking />
                    <div className="thinkingText">Thinking...</div>
                  </div>
                )
              }
              if (message.type === 'response') {
                return (
                  <div key={`response-${idx}`} className="responseContainer">
                    <div className="responseBubble">
                      <ResponseBlobby />
                      <ResponseContent
                        scrollToBottom={scrollToBottom}
                        blocks={message.content}
                      />
                    </div>
                  </div>
                )
              }
              return null
            })}

            {(isTyping || displayedText) && (
              <div className="typingContainer">
                <ChatInput
                  displayedText={displayedText}
                  isTyping={isTyping && !showNudge}
                  showNudge={showNudge}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
